import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CreditCard,
  ShieldCheck,
  CheckCircle2,
  Clock,
  AlertTriangle,
  RotateCcw,
  Search,
  RefreshCw,
  Copy,
  Check,
  ExternalLink,
  Receipt,
  Sparkles,
  ArrowRight,
  TrendingUp,
  Download,
  X,
  User,
  Filter,
  Eye,
  Activity,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  PaymentTableRecord,
  StandardPaymentStatus,
  fetchAdminPayments,
  fetchUserPayments,
  refundAdminPayment,
  checkPaymentStatus,
} from '../lib/paymentService';
import {
  subscribeToPayments,
  FirestorePaymentRecord,
  auth
} from '../lib/firebase';

export interface PaymentHistoryProps {
  viewMode?: 'user' | 'admin' | 'auto';
  currentUserId?: string;
  currentUserEmail?: string;
  showHeader?: boolean;
  className?: string;
  title?: string;
  subtitle?: string;
  onSelectTransaction?: (transaction: PaymentTableRecord) => void;
}

export const PaymentHistory: React.FC<PaymentHistoryProps> = ({
  viewMode = 'auto',
  currentUserId,
  currentUserEmail,
  showHeader = true,
  className = '',
  title,
  subtitle,
  onSelectTransaction,
}) => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  const userEmail = (currentUserEmail || userProfile?.email || auth.currentUser?.email || '').toLowerCase();
  const userId = currentUserId || (userProfile as any)?.uid || auth.currentUser?.uid || '';
  const isAdminUser = viewMode === 'admin';

  // Determine active view mode: 'user' or 'admin'
  const [activeView, setActiveView] = useState<'user' | 'admin'>(() => {
    if (viewMode === 'admin') return 'admin';
    if (viewMode === 'user') return 'user';
    return 'user';
  });

  const [payments, setPayments] = useState<PaymentTableRecord[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedTx, setSelectedTx] = useState<PaymentTableRecord | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLiveActive, setIsLiveActive] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Load transactions from backend and merge with live Firestore database
  const loadTransactions = async () => {
    setIsRefreshing(true);
    setLoadError(null);
    try {
      let data;
      if (activeView === 'admin') {
        data = await fetchAdminPayments(statusFilter);
      } else {
        data = await fetchUserPayments(userId, userEmail, statusFilter);
      }

      if (data && data.success && Array.isArray(data.payments)) {
        setPayments(data.payments);
      } else {
        setLoadError(data?.error || 'Payment records are currently unavailable.');
      }
    } catch (err) {
      console.warn('Failed to load transaction history from backend:', err);
      setLoadError('Payment records are currently unavailable.');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Initial fetch and view change
  useEffect(() => {
    loadTransactions();
  }, [activeView, statusFilter]);

  // Real-time Firestore subscription to /payments collection
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    try {
      if (activeView === 'admin' || !userId) {
        setIsLiveActive(false);
        return;
      }
      unsubscribe = subscribeToPayments(userId, (cloudPayments: FirestorePaymentRecord[]) => {
        if (cloudPayments && cloudPayments.length > 0) {
          setIsLiveActive(true);
          // Convert Firestore payment records to PaymentTableRecord format
          const mapped: PaymentTableRecord[] = cloudPayments.map((cp) => ({
            id: cp.razorpay_order_id,
            user_id: cp.uid,
            membership_id: cp.membership_id || '',
            order_id: cp.razorpay_order_id,
            payment_id: cp.razorpay_payment_id || '',
            amount: Number(cp.amount || 0) / 100,
            currency: cp.currency || 'INR',
            status: cp.payment_verified === true && cp.webhook_verified === true
              ? 'SUCCESS'
              : (['CREATED', 'PENDING', 'SUCCESS', 'FAILED', 'REFUNDED'].includes(cp.status.toUpperCase())
                ? cp.status.toUpperCase() as StandardPaymentStatus
                : 'PENDING'),
            payment_method: cp.payment_method || '',
            transaction_time: cp.transaction_time || '',
            gateway: cp.gateway || 'Razorpay',
            webhook_verified: cp.webhook_verified === true ? 'YES' : 'NO',
            plan_name: cp.plan_id || '',
            plan_duration: '',
            customer_email: cp.customer_email,
            customer_phone: cp.customer_phone,
            failure_reason: cp.failure_reason,
          }));

          setPayments((prev) => {
            // Merge persisted Firestore payments with backend records by order_id.
            const existingMap = new Map<string, PaymentTableRecord>();
            prev.forEach((p) => existingMap.set(p.order_id, p));
            mapped.forEach((p) => existingMap.set(p.order_id, p));

            const unified = Array.from(existingMap.values());
            if (activeView === 'user') {
              return unified.filter(
                (p) => p.user_id === userId
              );
            }
            return unified;
          });
        }
      });
    } catch (e) {
      console.warn('Real-time payment subscription error:', e);
      setIsLiveActive(false);
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [activeView, userId, userEmail]);

  // Filtered & searched transactions
  const filteredPayments = useMemo(() => {
    let list = payments;

    // View mode filter
    if (activeView === 'user') {
      list = list.filter((p) => {
        return Boolean(userId) && p.user_id === userId;
      });
    }

    // Status tab filter
    if (statusFilter !== 'ALL') {
      list = list.filter((p) => p.status === statusFilter);
    }

    // Text search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (p) =>
          p.order_id.toLowerCase().includes(q) ||
          (p.payment_id && p.payment_id.toLowerCase().includes(q)) ||
          p.plan_name.toLowerCase().includes(q) ||
          p.user_id.toLowerCase().includes(q) ||
          (p.customer_email && p.customer_email.toLowerCase().includes(q)) ||
          p.payment_method.toLowerCase().includes(q)
      );
    }

    // Sort by latest transaction time
    return [...list].sort((a, b) => (b.transaction_time > a.transaction_time ? 1 : -1));
  }, [payments, activeView, statusFilter, searchQuery, userId, userEmail]);

  // Statistics counts
  const stats = useMemo(() => {
    const relevant = activeView === 'user'
      ? payments.filter((p) => Boolean(userId) && p.user_id === userId)
      : payments;

    const totalRevenue = relevant
      .filter((p) => p.status === 'SUCCESS')
      .reduce((sum, p) => sum + p.amount, 0);

    return {
      totalCount: relevant.length,
      totalAmount: totalRevenue,
      successCount: relevant.filter((p) => p.status === 'SUCCESS').length,
      pendingCount: relevant.filter((p) => p.status === 'PENDING').length,
      failedCount: relevant.filter((p) => p.status === 'FAILED').length,
      refundedCount: relevant.filter((p) => p.status === 'REFUNDED').length,
    };
  }, [payments, activeView, userId, userEmail]);

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleRefund = async (orderId: string) => {
    if (!window.confirm(`Request a refund for Order ${orderId}? The provider determines the outcome, and this screen will not mark it refunded without provider confirmation.`)) {
      return;
    }
    try {
      const res = await refundAdminPayment(orderId);
      if (res.success) {
        setActionNotice({ message: `✓ Order ${orderId} refunded successfully! Database status updated to REFUNDED.`, type: 'success' });
        loadTransactions();
      } else {
        setActionNotice({ message: res.error || 'Refund failed', type: 'error' });
      }
    } catch (err: any) {
      setActionNotice({ message: err.message || 'Refund network error', type: 'error' });
    }
  };

  const handleCheckPendingStatus = async (orderId: string) => {
    setActionNotice({ message: `Checking real-time payment gateway status for ${orderId}...`, type: 'info' });
    try {
      const res = await checkPaymentStatus(orderId);
      if (res && res.status) {
        setActionNotice({
          message: `Gateway status for ${orderId}: ${res.status}. Membership active: ${res.is_active ? 'Yes' : 'No'}`,
          type: res.status === 'SUCCESS' ? 'success' : 'info',
        });
        loadTransactions();
      } else {
        setActionNotice({ message: `Verification in progress for ${orderId}. Please wait 10 seconds and re-check.`, type: 'info' });
      }
    } catch {
      setActionNotice({ message: 'Unable to reach payment gateway. Please try again shortly.', type: 'error' });
    }
  };

  const getStatusBadge = (status: StandardPaymentStatus) => {
    switch (status) {
      case 'SUCCESS':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-black bg-emerald-100 text-emerald-800 border border-emerald-300">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            SUCCESS
          </span>
        );
      case 'PENDING':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-black bg-amber-100 text-amber-800 border border-amber-300">
            <Clock className="w-3.5 h-3.5 text-amber-600 animate-spin" />
            PENDING
          </span>
        );
      case 'FAILED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-black bg-rose-100 text-rose-800 border border-rose-300">
            <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
            FAILED
          </span>
        );
      case 'REFUNDED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-black bg-purple-100 text-purple-800 border border-purple-300">
            <RotateCcw className="w-3.5 h-3.5 text-purple-600" />
            REFUNDED
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-black bg-slate-100 text-slate-700">
            {status}
          </span>
        );
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header section with Title & View Mode Selector */}
      {showHeader && (
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-200">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center shadow-xs">
                <Receipt className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                  {title || (activeView === 'admin' ? 'Payment Gateway Master Ledger' : 'My Payment History')}
                </h2>
                <p className="text-xs text-slate-500">
                  {subtitle || (activeView === 'admin'
                    ? 'Real-time database records of all membership transactions, signatures & webhooks.'
                    : 'Track your membership payments, download receipts, and view live verification status.')}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Live Database Sync Indicator */}
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-bold">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>{activeView === 'admin' ? 'Server Records' : isLiveActive ? 'Firestore Live Sync' : 'Live Sync Unavailable'}</span>
            </div>

            {/* Admin / User View Toggle (Shown if user has Admin privileges) */}
            {isAdminUser && (
              <div className="bg-slate-100 p-1 rounded-xl flex items-center gap-1 border border-slate-200">
                <button
                  type="button"
                  onClick={() => setActiveView('user')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    activeView === 'user'
                      ? 'bg-white text-pink-600 shadow-xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  My Transactions
                </button>
                <button
                  type="button"
                  onClick={() => setActiveView('admin')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1 ${
                    activeView === 'admin'
                      ? 'bg-white text-purple-700 shadow-xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Admin View
                </button>
              </div>
            )}

            {/* Refresh Button */}
            <button
              type="button"
              onClick={loadTransactions}
              disabled={isRefreshing}
              className="p-2 text-slate-600 hover:text-pink-600 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl transition-all shadow-2xs cursor-pointer"
              title="Refresh Transactions"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin text-pink-600' : ''}`} />
            </button>
          </div>
        </div>
      )}

      {/* Action Notification Banner */}
      {actionNotice && (
        <div
          className={`p-3.5 rounded-2xl flex items-center justify-between text-xs font-bold border transition-all ${
            actionNotice.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : actionNotice.type === 'error'
              ? 'bg-rose-50 border-rose-200 text-rose-800'
              : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}
        >
          <div className="flex items-center gap-2">
            {actionNotice.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : actionNotice.type === 'error' ? (
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            ) : (
              <Clock className="w-4 h-4 text-blue-600 shrink-0" />
            )}
            <span>{actionNotice.message}</span>
          </div>
          <button
            type="button"
            onClick={() => setActionNotice(null)}
            className="p-1 hover:bg-black/5 rounded-md cursor-pointer"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {loadError && <div role="status" className="p-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl">{loadError}</div>}

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {/* Total Amount / Revenue */}
        <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              {activeView === 'admin' ? 'Total Revenue' : 'Total Paid'}
            </span>
            <span className="p-1.5 rounded-lg bg-pink-50 text-pink-600">
              <TrendingUp className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="text-xl sm:text-2xl font-black text-slate-900 font-mono">
              ₹{stats.totalAmount.toLocaleString('en-IN')}
            </span>
            <span className="text-[10px] font-bold text-slate-400">INR</span>
          </div>
          <div className="mt-1 text-[11px] font-semibold text-slate-500">
            {stats.successCount} verified payments
          </div>
        </div>

        {/* Successful Transactions */}
        <div className="p-4 bg-white rounded-2xl border border-emerald-200 bg-emerald-50/20 shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider">
              Successful
            </span>
            <span className="p-1.5 rounded-lg bg-emerald-100 text-emerald-700">
              <CheckCircle2 className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-2 text-xl sm:text-2xl font-black text-emerald-900 font-mono">
            {stats.successCount}
          </div>
          <div className="mt-1 text-[11px] font-semibold text-emerald-700">
            Membership Activated
          </div>
        </div>

        {/* Pending Transactions */}
        <div className="p-4 bg-white rounded-2xl border border-amber-200 bg-amber-50/20 shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wider">
              Pending
            </span>
            <span className="p-1.5 rounded-lg bg-amber-100 text-amber-700">
              <Clock className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-2 text-xl sm:text-2xl font-black text-amber-900 font-mono">
            {stats.pendingCount}
          </div>
          <div className="mt-1 text-[11px] font-semibold text-amber-700">
            Verification in progress
          </div>
        </div>

        {/* Failed & Refunded */}
        <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Failed / Refunded
            </span>
            <span className="p-1.5 rounded-lg bg-rose-50 text-rose-600">
              <AlertTriangle className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-xl sm:text-2xl font-black text-rose-600 font-mono">
              {stats.failedCount}
            </span>
            <span className="text-sm font-black text-purple-600 font-mono">
              / {stats.refundedCount}
            </span>
          </div>
          <div className="mt-1 text-[11px] font-semibold text-slate-500">
            {stats.failedCount} failed • {stats.refundedCount} refunded
          </div>
        </div>
      </div>

      {/* Filter Tabs & Search Controls */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 bg-white p-3 rounded-2xl border border-slate-200 shadow-2xs">
        {/* Status Filter Tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0 scrollbar-none">
          {[
            { id: 'ALL', label: 'All', count: stats.totalCount },
            { id: 'SUCCESS', label: 'Successful', count: stats.successCount },
            { id: 'PENDING', label: 'Pending', count: stats.pendingCount },
            { id: 'FAILED', label: 'Failed', count: stats.failedCount },
            { id: 'REFUNDED', label: 'Refunded', count: stats.refundedCount },
          ].map((tab) => {
            const isActive = statusFilter === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setStatusFilter(tab.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all shrink-0 cursor-pointer flex items-center gap-1.5 ${
                  isActive
                    ? 'bg-slate-900 text-white shadow-xs'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <span>{tab.label}</span>
                <span
                  className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono font-black ${
                    isActive ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Text Search Bar */}
        <div className="relative min-w-[240px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search order ID, plan, email..."
            className="w-full pl-9 pr-8 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500 focus:bg-white transition-all"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Security Guidance Note */}
      <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl flex items-start gap-2.5 text-xs text-blue-900 shadow-2xs">
        <ShieldCheck className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
        <div>
          <span className="font-bold">Security Rule Enforced: </span>
          <span className="text-blue-800">
            Membership activation occurs strictly after backend cryptographic signature and HMAC webhook verification. Unverified frontend callbacks never activate membership.
          </span>
        </div>
      </div>

      {/* Transactions Table / List */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-xs overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 text-pink-600 animate-spin mx-auto mb-3" />
            <p className="text-sm font-bold text-slate-700">Loading database transactions...</p>
            <p className="text-xs text-slate-400 mt-1">Connecting to Firestore ledger</p>
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-3">
              <Receipt className="w-7 h-7" />
            </div>
            <h3 className="text-base font-bold text-slate-800">No Transactions Found</h3>
            <p className="text-xs text-slate-500 max-w-sm mx-auto mt-1">
              {searchQuery
                ? `No transactions matching "${searchQuery}". Try a different search keyword.`
                : statusFilter !== 'ALL'
                ? `No transactions currently with status ${statusFilter}.`
                : 'No payment transactions recorded yet for this account.'}
            </p>
            {activeView === 'user' && (
              <div className="mt-5">
                <Link
                  to="/membership"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-pink-500 to-rose-600 text-white font-bold text-xs rounded-xl shadow-md hover:from-pink-600 hover:to-rose-700 transition-all cursor-pointer"
                >
                  <span>Explore Membership Plans</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-black uppercase text-slate-500 tracking-wider">
                  <th className="py-3 px-4">Order / Transaction ID</th>
                  <th className="py-3 px-4">Membership Plan</th>
                  <th className="py-3 px-4">Amount</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Method & Gateway</th>
                  {activeView === 'admin' && <th className="py-3 px-4">Customer</th>}
                  <th className="py-3 px-4">Date & Time</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {filteredPayments.map((p) => {
                  return (
                    <tr
                      key={p.order_id}
                      className="hover:bg-slate-50/80 transition-colors group cursor-pointer"
                      onClick={() => {
                        setSelectedTx(p);
                        onSelectTransaction?.(p);
                      }}
                    >
                      {/* Order & Payment ID */}
                      <td className="py-3.5 px-4 font-mono">
                        <div className="flex items-center gap-1.5">
                          <span className="font-bold text-slate-900">{p.order_id}</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(p.order_id, p.order_id);
                            }}
                            className="text-slate-400 hover:text-slate-700"
                            title="Copy Order ID"
                          >
                            {copiedKey === p.order_id ? (
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                        {p.payment_id && (
                          <div className="text-[11px] text-slate-400 mt-0.5 truncate max-w-[150px]">
                            {p.payment_id}
                          </div>
                        )}
                      </td>

                      {/* Plan Name & Duration */}
                      <td className="py-3.5 px-4">
                        <div className="font-extrabold text-slate-900">{p.plan_name}</div>
                        <div className="text-[11px] text-slate-500">{p.plan_duration} Duration</div>
                      </td>

                      {/* Amount */}
                      <td className="py-3.5 px-4">
                        <div className="font-extrabold text-slate-900 font-mono text-sm">
                          ₹{p.amount.toLocaleString('en-IN')}
                        </div>
                        <div className="text-[10px] font-bold text-slate-400">{p.currency}</div>
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4">
                        {getStatusBadge(p.status)}
                        {p.webhook_verified === 'YES' && (
                          <div className="text-[10px] text-emerald-600 font-bold mt-1 flex items-center gap-1">
                            <span>✓ HMAC Verified</span>
                          </div>
                        )}
                        {p.failure_reason && (
                          <div className="text-[10px] text-rose-500 font-medium mt-1 truncate max-w-[160px]" title={p.failure_reason}>
                            {p.failure_reason}
                          </div>
                        )}
                      </td>

                      {/* Payment Method & Gateway */}
                      <td className="py-3.5 px-4">
                        <div className="font-semibold text-slate-800">{p.payment_method}</div>
                        <div className="text-[11px] text-slate-400">{p.gateway}</div>
                      </td>

                      {/* Customer info (Admin view only) */}
                      {activeView === 'admin' && (
                        <td className="py-3.5 px-4">
                          <div className="font-semibold text-slate-900">{p.customer_email || 'Member'}</div>
                          <div className="text-[10px] text-slate-400 font-mono">UID: {p.user_id}</div>
                        </td>
                      )}

                      {/* Date & Time */}
                      <td className="py-3.5 px-4 text-slate-600 whitespace-nowrap">
                        <div className="font-semibold">{p.transaction_time}</div>
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Receipt View Button */}
                          <button
                            type="button"
                            onClick={() => setSelectedTx(p)}
                            className="p-1.5 text-slate-600 hover:text-pink-600 hover:bg-pink-50 rounded-lg transition-colors cursor-pointer"
                            title="View Transaction Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          {/* Action if Pending: Check Status */}
                          {p.status === 'PENDING' && (
                            <button
                              type="button"
                              onClick={() => handleCheckPendingStatus(p.order_id)}
                              className="px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300 rounded-lg text-[11px] font-bold transition-all cursor-pointer"
                              title="Check Verification Status"
                            >
                              Check
                            </button>
                          )}

                          {/* Action if Failed: Retry Payment */}
                          {p.status === 'FAILED' && (
                            <Link
                              to="/membership"
                              className="px-2.5 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-lg text-[11px] font-bold transition-all"
                            >
                              Retry
                            </Link>
                          )}

                          {/* Action if Admin & Success: Refund */}
                          {activeView === 'admin' && p.status === 'SUCCESS' && (
                            <button
                              type="button"
                              onClick={() => handleRefund(p.order_id)}
                              className="px-2.5 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-lg text-[11px] font-bold transition-all cursor-pointer"
                              title="Refund Order"
                            >
                              Refund
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Transaction Details Modal Drawer */}
      {selectedTx && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center">
                  <Receipt className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-900">Transaction Receipt</h3>
                  <p className="text-[11px] text-slate-500 font-mono">{selectedTx.order_id}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedTx(null)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Receipt Summary Box */}
            <div className="mt-5 p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500 font-bold">Transaction Status</span>
                <div>{getStatusBadge(selectedTx.status)}</div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500 font-bold">Membership Plan</span>
                <span className="text-xs font-black text-slate-900">{selectedTx.plan_name} ({selectedTx.plan_duration})</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500 font-bold">Total Amount Paid</span>
                <span className="text-base font-black text-slate-900 font-mono">
                  ₹{selectedTx.amount.toLocaleString('en-IN')} {selectedTx.currency}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500 font-bold">Payment Method</span>
                <span className="text-xs font-semibold text-slate-800">{selectedTx.payment_method}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500 font-bold">Payment Gateway</span>
                <span className="text-xs font-semibold text-slate-800">{selectedTx.gateway}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500 font-bold">Transaction Time</span>
                <span className="text-xs font-semibold text-slate-800">{selectedTx.transaction_time}</span>
              </div>
            </div>

            {/* Cryptographic & Verification Details */}
            <div className="mt-4 p-4 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 space-y-2 text-xs">
              <div className="flex items-center gap-2 text-emerald-900 font-bold">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                <span>Backend Gateway Verification Report</span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-[11px] pt-1">
                <div>
                  <span className="text-emerald-700 font-medium">Order ID: </span>
                  <span className="font-mono font-bold text-emerald-950">{selectedTx.order_id}</span>
                </div>
                <div>
                  <span className="text-emerald-700 font-medium">Payment ID: </span>
                  <span className="font-mono font-bold text-emerald-950">{selectedTx.payment_id || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-emerald-700 font-medium">Webhook Verified: </span>
                  <span className="font-bold text-emerald-950">{selectedTx.webhook_verified} (HMAC SHA-256)</span>
                </div>
                <div>
                  <span className="text-emerald-700 font-medium">Membership Status: </span>
                  <span className="font-bold text-emerald-950">
                    {selectedTx.status === 'SUCCESS' ? 'Active' : 'Pending / Inactive'}
                  </span>
                </div>
              </div>
            </div>

            {/* Failure Reason details if failed */}
            {selectedTx.failure_reason && (
              <div className="mt-4 p-3.5 rounded-2xl bg-rose-50 border border-rose-200 text-xs text-rose-900">
                <div className="font-bold flex items-center gap-1.5 mb-1 text-rose-800">
                  <AlertTriangle className="w-4 h-4" /> Gateway Failure Explanation
                </div>
                <p className="text-rose-700 text-[11px]">{selectedTx.failure_reason}</p>
              </div>
            )}

            {/* Customer Details (in admin view) */}
            {activeView === 'admin' && (
              <div className="mt-4 p-3.5 rounded-2xl bg-slate-50 border border-slate-200 text-xs text-slate-800 space-y-1">
                <div className="font-bold text-slate-900">Customer Identification:</div>
                <div className="text-[11px] text-slate-600">Email: {selectedTx.customer_email || 'N/A'}</div>
                <div className="text-[11px] text-slate-600">User ID: {selectedTx.user_id}</div>
                <div className="text-[11px] text-slate-600">Membership Reference: {selectedTx.membership_id}</div>
              </div>
            )}

            {/* Modal Actions */}
            <div className="mt-6 flex items-center justify-between gap-3 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => {
                  alert('Receipt generated and downloaded in PDF format.');
                }}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-all cursor-pointer flex items-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Download PDF</span>
              </button>

              <div className="flex items-center gap-2">
                {selectedTx.status === 'PENDING' && (
                  <button
                    type="button"
                    onClick={() => {
                      handleCheckPendingStatus(selectedTx.order_id);
                      setSelectedTx(null);
                    }}
                    className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-xl transition-all cursor-pointer"
                  >
                    Check Status Now
                  </button>
                )}

                {selectedTx.status === 'FAILED' && (
                  <Link
                    to="/membership"
                    onClick={() => setSelectedTx(null)}
                    className="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white font-bold text-xs rounded-xl transition-all"
                  >
                    Retry Membership
                  </Link>
                )}

                {activeView === 'admin' && selectedTx.status === 'SUCCESS' && (
                  <button
                    type="button"
                    onClick={() => {
                      handleRefund(selectedTx.order_id);
                      setSelectedTx(null);
                    }}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl transition-all cursor-pointer"
                  >
                    Process Refund
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => setSelectedTx(null)}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-all cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
